import { AbstractControl, FormArray, FormControl, FormGroup, Validators } from '@angular/forms';
import { FormControlModel, PROMPTS, StepperGroupModel } from '@thehubz/client/core';
import { Territories } from '@thehubz/client/data-access-api';
import {
  AnyType,
  AssetMetadata,
  MediaCategories,
  MediaLists,
  TerritoryCategories,
  TerritoryCategoriesLufthansaGroup,
  TerritoryList,
} from '@thehubz/shared/model';

export function getRemovedValues(oldValues: AnyType[], newValues: AnyType[]): AnyType[] {
  return oldValues.filter((oldValue) => !newValues.some((value) => oldValue._id === value._id));
}

export function getTerritoriesToRemove(territories: AnyType[], removedValues: AnyType[]): AnyType[] {
  return territories.filter((territory) => removedValues.some((value) => territory.categories.includes(value._id)));
}

export function getTerritoriesToAdd(territories: AnyType[], selectedValues: AnyType[]): AnyType[] {
  return territories.filter((territory) => selectedValues.some((value) => territory.categories.includes(value._id)));
}

export function mapTerritoryAndMedia(item: Territories) {
  const mapWithFallback = (id: string, list: { _id: string; name: string }[]) => {
    const found = list.find((x) => x._id === id);
    return found ? { _id: found._id, name: found.name.trim() } : { _id: id, name: id };
  };

  return {
    ...item,
    territory: item.territory?.map((id: string) => mapWithFallback(id, [...TerritoryCategories, ...TerritoryCategoriesLufthansaGroup, ...TerritoryList])),
    media: item.media?.map((id: string) => mapWithFallback(id, [...MediaCategories, ...MediaLists])),
  };
}

export function addSubLevelTypeToBuyoutRight(formValueObject: AnyType) {
  if (formValueObject.usageRightsGeneralInformation?.buyoutRightsSecondLevelType) {
    for (const key in formValueObject.usageRightsGeneralInformation.buyoutRightsSecondLevelType) {
      if (formValueObject.usageRightsGeneralInformation.buyoutRightsFormArray[key]) {
        formValueObject.usageRightsGeneralInformation.buyoutRightsFormArray[key].subLevelType =
          formValueObject.usageRightsGeneralInformation.buyoutRightsSecondLevelType[key];
      }
    }
    delete formValueObject.usageRightsGeneralInformation.buyoutRightsSecondLevelType;
  }
}

export function mergeSecondLevelBuyoutRightsType(original: { [key: string]: string }, toMerge: { [key: string]: string }): { [key: string]: string } {
  const result = { ...original };

  // Get the next available numeric key
  const existingKeys = Object.keys(result).map(Number);
  const nextIndex = existingKeys.length > 0 ? Math.max(...existingKeys) + 1 : 0;

  let currentIndex = nextIndex;

  for (const key of Object.keys(toMerge)) {
    result[currentIndex.toString()] = toMerge[key];
    currentIndex++;
  }

  return result;
}

export function updateFormFieldConfig(step: StepperGroupModel): void {
  const secondLevelFieldIndex = step.formFields?.findIndex((f) => f.formFieldName === AssetMetadata.Rights.BUYOUT_RIGHTS_SECOND_LEVEL_TYPE);
  if (secondLevelFieldIndex !== undefined && secondLevelFieldIndex !== -1) {
    step.formFields?.splice(secondLevelFieldIndex, 1);
  }

  const titleFieldIndex = step.formFields?.findIndex((f) => f.formFieldName === AssetMetadata.Rights.BUYOUT_RIGHTS_TITLE);
  if (titleFieldIndex !== undefined && titleFieldIndex !== -1 && step.formFields) {
    step.formFields[titleFieldIndex].disable = false;
  }
}

export function deepCloneControl<T extends AbstractControl>(control: T): T {
  let clone: AbstractControl;

  if (control instanceof FormGroup) {
    const group = new FormGroup({}, control.validator, control.asyncValidator);
    for (const key of Object.keys(control.controls)) {
      group.addControl(key, deepCloneControl(control.controls[key]));
    }
    clone = group;
  } else if (control instanceof FormArray) {
    const array = new FormArray<AbstractControl>([], control.validator, control.asyncValidator);
    control.controls.forEach((child) => {
      array.push(deepCloneControl(child) as AbstractControl);
    });
    clone = array;
  } else if (control instanceof FormControl) {
    clone = new FormControl(control.value, control.validator, control.asyncValidator);
  } else {
    throw new Error('Unsupported AbstractControl type');
  }

  copyControlState(control, clone);
  return clone as T;
}

function copyControlState(from: AbstractControl, to: AbstractControl): void {
  if (from.disabled) {
    to.disable({ emitEvent: false });
  } else {
    to.enable({ emitEvent: false });
  }

  if (from.dirty) {
    to.markAsDirty({ onlySelf: true });
  }

  if (from.touched) {
    to.markAsTouched({ onlySelf: true });
  }

  if (from.pending) {
    to.markAsPending({ onlySelf: true });
  }

  if (from.errors) {
    to.setErrors({ ...from.errors }, { emitEvent: false });
  }
}

export function setControlPlaceholder(control: AbstractControl, placeholder: string) {
  (control as AnyType).__placeholder = placeholder;
}

export function getControlPlaceholder(control: AbstractControl, defaultPlaceholder: string) {
  return (control as AnyType).__placeholder || defaultPlaceholder;
}

export function getFormFieldGroupByName(fields: FormControlModel[], targetFieldName: string): FormControlModel[] | undefined {
  const target = fields.find((f) => f.formFieldName === targetFieldName);
  return target?.formFieldGroup;
}

export function updateMultipleValuesInTerritories(
  obj: Record<string, AnyType>,
  formArrayControls: AbstractControl,
  territoriesFormArrayFields?: FormControlModel[],
  targetValue = 'Multiple-values',
) {
  for (const key in obj) {
    const value = obj[key];
    const control = formArrayControls.get(key);

    if (!control) return;
    let hasMultipleValues = false;

    if (Array.isArray(value)) hasMultipleValues = value.some((item) => item?._id === targetValue);
    else if (value && typeof value === 'string') hasMultipleValues = value === targetValue;

    if (hasMultipleValues) {
      control.patchValue(null);
      control.removeValidators([Validators.required]);
      control.updateValueAndValidity();
      setControlPlaceholder(control, PROMPTS.TEXT.MULTI_VALUE);
    } else {
      control.patchValue(value);
      setControlPlaceholder(control, territoriesFormArrayFields?.find((f) => f.formFieldName === key)?.placeholder || '');
    }
  }
}

/**
 * This function is used to transform form data with placeholders for null values.
 * It checks if the value is an array or an object and recursively processes each item or property.
 * If a value is null and the corresponding control has a placeholder, it uses that placeholder.
 * If the value is null and no placeholder is defined, it uses a default text for multiple values.
 * @param formValue
 * @param formGroup
 * @param multiValueText
 * @returns
 */
export function transformFormDataWithPlaceholders(formValue: AnyType, formGroup: AbstractControl, multiValueText = 'Multiple values'): AnyType {
  if (Array.isArray(formValue)) {
    return formValue.map((item, idx) =>
      transformFormDataWithPlaceholders(
        item,
        formGroup && typeof (formGroup as AnyType).at === 'function' ? (formGroup as AnyType).at(idx) : undefined,
        multiValueText,
      ),
    );
  } else if (formValue && typeof formValue === 'object' && !(formValue instanceof Date)) {
    const result: AnyType = {};
    for (const key in formValue) {
      const control = formGroup && typeof (formGroup as AnyType).get === 'function' ? (formGroup as AnyType).get(key) : undefined;
      if (formValue[key] === null && control && control.__placeholder) {
        result[key] = control.__placeholder;
      } else if (formValue[key] === null) {
        result[key] = multiValueText;
      } else {
        result[key] = transformFormDataWithPlaceholders(formValue[key], control, multiValueText);
      }
    }
    return result;
  } else if (formValue instanceof Date) {
    return formValue.toString();
  }
  return formValue;
}
